<template>
  <div class="h-100 d-flex flex-column align-center justify-space-around" ref="outerDiv">
    <div
      v-if="system"
      :id="`system-${system.index}`"
      :class="{'elevation-1 service-border pa-2': hasRelatedEntities}"
      class="position-relative"
      style="z-index: 1"
    >
      <div>
        <div
          v-if="system.groupedBy"
          class="requirement-card hover-container fit-content"
          :style="{ 'border-color': cardTypeColor(entityCardType) }"
        >
          <div :id="system.id">
            <select-entity-combobox
              :events="system.events"
              :boundedContextId="boundedContext.id"
              :cardIds="system.entityCards.map((card) => card.id)"
              :style="{ '--borderColor': cardTypeColor(entityCardType), '--borderTopWidth': '1px' }"
            />
          </div>
          <div v-if="showEntities && currentSystemEntitiesData.rootEntity?.dataFields?.length > 0" class="py-2">
            <div
              v-for="(field, index4) in currentSystemEntitiesData.rootEntity.dataFields"
              :key="index4"
              class="field-name d-flex align-center position-relative"
            >
              <div
                v-if="currentSystemEntitiesData.rootEntity.dataFields?.some((item) => item.primaryKey || item.reference)"
                class="field-icon-container"
              >
                <div v-if="field.primaryKey || field.reference" class="d-inline-flex align-center field-icon-container">
                  <v-tooltip v-if="field.primaryKey" location="bottom" open-delay="500">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="16" icon="mdi-key" color="#4f5359" />
                    </template>
                    <span>Primary key</span>
                  </v-tooltip>
                  <v-tooltip v-else location="bottom" open-delay="500">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="16" icon="mdi-link" color="#4f5359" />
                    </template>
                    <span>Reference to entity</span>
                  </v-tooltip>
                </div>
              </div>
              <quick-rename-input
                :id="`root-entity-${currentSystemEntitiesData.rootEntity.id}-field-${index4}`"
                :value="field.name"
                @onChange="blurEntityField($event, currentSystemEntitiesData.rootEntity, index4)"
                :density="`compact`"
                :max-length="30"
                class="flex-1-0"
                :class="{ 'required-field': field.tags?.includes('REQUIRED') }"
                :disabled="store.state.isWorkflowDisabled"
              />
              <v-tooltip v-if="field.reference && !getRelatedEntity(field)" location="bottom" open-delay="100">
                <template v-slot:activator="{ props }">
                  <v-icon v-bind="props" size="small" icon="mdi-alert" color="#ED1C0C" />
                </template>
                <span>Reference is missing related entity</span>
              </v-tooltip>
              <v-chip v-if="field.relatedEntityId && getRelatedEntity(field)" density="compact" size="small" class="px-2 ml-1">
                {{ field.cardinality === 'one-to-many'
                  ? `[ ${getRelatedEntity(field)?.name} ]`
                  : `${getRelatedEntity(field)?.name}` }}
              </v-chip>
            </div>
          </div>
          <div class="d-flex justify-space-between align-center ga-2">
            <div class="show-on-hover">
              <edit-entity
                v-if="currentSystemEntitiesData.rootEntity"
                :entity="currentSystemEntitiesData.rootEntity"
                :refreshKey="refreshKey"
              />
            </div>
            <workflow-aggregate-select-bounded-context
              :requirements="currentSystemEntitiesData.rootEntity ? null : system.entityCards"
              :entity="currentSystemEntitiesData.rootEntity || null"
              :boundedContext="boundedContext"
            />
          </div>
        </div>
        <v-btn
          v-else-if="entityCardType"
          class="add-card-btn"
          variant="elevated"
          block
          :style="{ '--border-color': cardTypeColor(entityCardType) }"
          @click="createCard(system.events[0].id, entityCardType.id, `<p>Aggregate Root for ${system.events[0].description}</p>`)"
          :disabled="store.state.isWorkflowDisabled"
        >
          + {{ entityCardType.title }}
        </v-btn>
        <aggregate-type-selector
          v-else
          :requirementTypes="requirementTypes"
          type="aggregate"
        />

        <!-- System: entities under aggregate root -->
        <div
          v-if="showEntities && system.groupedBy && findEntitiesInBoundedContext(system.groupedBy.boundedContext).length > 0"
          class="d-flex justify-space-between ga-4"
        >
          <template v-for="columnName in ['left', 'right']" :key="columnName">
            <div class="d-flex flex-column justify-space-between align-center w-100" style="min-width: 150px; font-size: 13px;">
              <template v-for="(entity, index) in groupedEntities(columnName, system.groupedBy.boundedContext)" :key="index">
                <v-card
                  :id="`${system.id}-${columnName}-end-element-${entity.name}`"
                  class="d-flex flex-column align-start mt-5 bg-white hover-container w-100"
                  :ref="`left-entities-${index}`"
                >
                  <v-card-text
                    class="requirement-card-body rounded-lg pa-1 hover-container"
                    :style="{
                      'border-color': cardTypeColor(entityCardType),
                      'border-radius': '1px',
                      'font-size': '13px !important',
                    }"
                  >
                    <div class="d-flex justify-space-between align-center ga-2 mb-1">
                      {{ entity.name }}
                      <div class="show-on-hover pe-2">
                        <edit-entity :entity="entity" :refreshKey="refreshKey" />
                      </div>
                    </div>
                    <template v-if="showEntities">
                      <v-divider thickness="2" style="width: 100%;"></v-divider>
                      <template v-for="(field, index2) in entity" :key="index2">
                        <div v-if="field && field.name" class="field-name d-flex align-center">
                          <div v-if="field.primaryKey || field.reference" class="d-inline-flex align-center">
                            <v-tooltip v-if="field.primaryKey" location="bottom" open-delay="500">
                              <template v-slot:activator="{ props }">
                                <v-icon v-bind="props" size="16" icon="mdi-key" color="#4f5359" />
                              </template>
                              <span>Primary key</span>
                            </v-tooltip>
                            <v-tooltip v-else location="bottom" open-delay="500">
                              <template v-slot:activator="{ props }">
                                <v-icon v-bind="props" size="16" icon="mdi-link" color="#4f5359" />
                              </template>
                              <span>Reference to entity</span>
                            </v-tooltip>
                          </div>
                          <quick-rename-input
                            :id="`entity-${entity.id}-field-${index2}`"
                            :value="field.name"
                            @onChange="(e) => blurEntityField(e, entity, index2)"
                            :density="`compact`"
                            :max-length="30"
                            class="flex-1-0"
                            :class="{ 'required-field': field.tags?.includes('REQUIRED') }"
                            :disabled="store.state.isWorkflowDisabled"
                          />
                        </div>
                      </template>
                    </template>
                    <workflow-aggregate-select-bounded-context
                      :entity="entity"
                      :boundedContext="boundedContext"
                    />
                  </v-card-text>
                </v-card>
                <connecting-path
                  :endElementId="`${system.id}-${columnName}-end-element-${entity.name}`"
                  :startElementId="system.id"
                  :refreshKey="refreshKey"
                  :multiline="entity.isOneToMany"
                  start="bottom"
                  end="top"
                />
              </template>
            </div>
          </template>
        </div>
      </div>

      <!-- System: entities under integration with other contexts -->
      <template v-if="showEntities && system.groupedBy && findEntitiesNotInBoundedContext(system.groupedBy.boundedContext).length > 0">
        <div class="integration-divider text-white my-2 mb-0">Integration with other bounded contexts</div>
        <div class="d-flex justify-space-between ga-4">
          <template v-for="columnName in ['left', 'right']" :key="columnName">
            <div class="d-flex flex-column justify-space-between align-center w-100" style="min-width: 150px; font-size: 13px;">
              <template v-for="(entity, index) in groupedEntities(columnName, system.groupedBy.boundedContext, true)" :key="index">
                <v-card
                  :id="`${system.id}-integration-${columnName}-end-element-${entity.name}`"
                  class="d-flex flex-column align-start mt-5 bg-white hover-container"
                  min-width="150"
                  :ref="`left-entities-${index}`"
                >
                  <v-card-text
                    class="requirement-card-body rounded-lg pa-1 hover-container"
                    :style="{
                      'border-color': '#868686',
                      'border-radius': '1px',
                      'font-size': '13px !important',
                    }"
                  >
                    <div class="d-flex justify-space-between align-center ga-2 mb-1">
                      {{ entity.name }}
                      <div class="show-on-hover pe-2">
                        <edit-entity :entity="entity" :refreshKey="refreshKey" />
                      </div>
                    </div>
                    <template v-if="showEntities">
                      <v-divider thickness="2" style="width: 100%;"></v-divider>
                      <template v-for="(field, index2) in entity" :key="index2">
                        <div v-if="field && field.name" class="field-name d-flex align-center">
                          <div v-if="field.primaryKey || field.reference" class="d-inline-flex align-center">
                            <v-tooltip v-if="field.primaryKey" location="bottom" open-delay="500">
                              <template v-slot:activator="{ props }">
                                <v-icon v-bind="props" size="16" icon="mdi-key" color="#4f5359" />
                              </template>
                              <span>Primary key</span>
                            </v-tooltip>
                            <v-tooltip v-else location="bottom" open-delay="500">
                              <template v-slot:activator="{ props }">
                                <v-icon v-bind="props" size="16" icon="mdi-link" color="#4f5359" />
                              </template>
                              <span>Reference to entity</span>
                            </v-tooltip>
                          </div>
                          <quick-rename-input
                            :id="`entity-${entity.id}-field-${index2}`"
                            :value="field.name"
                            @onChange="(e) => blurEntityField(e, entity, index2)"
                            :density="`compact`"
                            :max-length="30"
                            class="flex-1-0"
                            :class="{ 'required-field': field.tags?.includes('REQUIRED') }"
                            :disabled="store.state.isWorkflowDisabled"
                          />
                        </div>
                      </template>
                    </template>
                    <workflow-aggregate-select-bounded-context
                      :entity="entity"
                      :boundedContext="boundedContext"
                    />
                  </v-card-text>
                </v-card>
                <connecting-path
                  :endElementId="`${system.id}-integration-${columnName}-end-element-${entity.name}`"
                  :startElementId="system.id"
                  :refreshKey="refreshKey"
                  :multiline="entity.isOneToMany"
                  start="bottom"
                  end="top"
                />
              </template>
            </div>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useStore } from 'vuex'
import EditEntity from '@/components/EditEntity'
import ConnectingPath from '@/components/ConnectingPath'
import QuickRenameInput from '@/components/QuickRenameInput'
import SelectEntityCombobox from '@/components/SelectEntityCombobox'
import AggregateTypeSelector from '@/components/AggregateTypeSelector'
import WorkflowAggregateSelectBoundedContext from '@/components/WorkflowAggregateSelectBoundedContext'
import workflowApi from '@/api/workflowApi'
import safeStructuredClone from '@/utils/clone'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import useDomainModel from '@/composables/useDomainModel'

const { system, refreshKey, boundedContext, showEntities, currentSystemEntitiesData } = defineProps({
  system: Object,
  refreshKey: Number,
  boundedContext: Object,
  showEntities: Boolean,
  currentSystemEntitiesData: Object
})
const emit = defineEmits(['refreshArrows'])

const outerDiv = ref(null)
const resizeObserver = ref(null)

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)
const requirementTypes = computed(() => store.state.requirementTypes.requirementTypes)
const entityCardType = computed(() => diagramHelpers.methods.getAggregateCardType(requirementTypes.value))

const hasRelatedEntities = computed(() => {
  return system.groupedBy && showEntities && currentSystemEntitiesData.entities.length > 0
})

const { validateFieldName, cardTypeColor, getRelatedEntity, createCard } = useDomainModel()

const groupedEntities = (columnName, boundedContext, notInBoundedContext = false) => {
  const res = []
  let index = 0
  const rootEntity = currentSystemEntitiesData.rootEntity

  if (workflow.value.dataModels) {
    let entities
    if (notInBoundedContext) {
      entities = findEntitiesNotInBoundedContext(boundedContext)
    } else {
      entities = findEntitiesInBoundedContext(boundedContext)
    }
    for (const entity of entities) {
      const matchingFieldInRoot = rootEntity
        ? rootEntity.dataFields?.find((item) => item.relatedEntityId === entity.id)
        : null

      if (index % 2 === 0 && columnName === 'left') {
        res.push({
          ...entity,
          ...entity.dataFields,
          name: entity.name,
          isOneToMany: matchingFieldInRoot?.cardinality === 'one-to-many',
          index
        })
      } else if (index % 2 !== 0 && columnName === 'right') {
        res.push({
          ...entity,
          ...entity.dataFields,
          name: entity.name,
          isOneToMany: matchingFieldInRoot?.cardinality === 'one-to-many',
          index
        })
      }
      index++
    }
  }
  return res
}

const findEntitiesInBoundedContext = (boundedContext) => {
  return currentSystemEntitiesData.entities?.filter(model => model.boundedContext === boundedContext)
}

const findEntitiesNotInBoundedContext = (boundedContext) => {
  return currentSystemEntitiesData.entities?.filter(model => model.boundedContext !== boundedContext)
}

const blurEntityField = async (e, entity, indexEntityField) => {
  if (!system.events?.length) {
    return
  }

  const indexDataModel = workflow.value.dataModels.findIndex((item) => item.name === entity.name)

  if (indexDataModel === -1 || Number(indexEntityField) === -1) {
    return
  }

  const eventId = system.events[0].id
  const fieldName = 'name'

  const newDataModels = safeStructuredClone(workflow.value.dataModels)

  const newName = (e.target.innerText || '').trim()
  const oldName = newDataModels[indexDataModel].dataFields[indexEntityField][fieldName]

  if (newName === oldName || !validateFieldName(newName, e, oldName)) {
    return
  }

  newDataModels[indexDataModel].dataFields[indexEntityField][fieldName] = newName

  await workflowApi.updateDataModels({
    eventId,
    dataModelsOnWorkflow: newDataModels,
    workflow: workflow.value
  })
}

const handleResize = () => {
  requestAnimationFrame(() => {
    emit('refreshArrows')
  })
}

onMounted(() => {
  if (outerDiv.value) {
    resizeObserver.value = new ResizeObserver(handleResize)
    resizeObserver.value.observe(outerDiv.value)
  }
})

onUnmounted(() => {
  if (resizeObserver.value) {
    resizeObserver.value.disconnect()
  }
})
</script>

<style scoped>
.service-border {
  border-radius: 10px;
  background-color: rgb(var(--v-theme-surface));
  border: 1px solid gray;
}

.requirement-card-body {
  border-width: 1px;
  border-top-width: 8px;
  border-style: solid;
  width: 100%;
}

.field-icon-container {
  width: 16px;
  margin-right: 2px;
}

.required-field {
  &::after {
    content: ' *';
  }

  &:focus::after {
    visibility: hidden;
  }
}
</style>
